import React, { Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';
import ToolTip from 'src/ui/library/ToolTip';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import styled from 'src/ui/library/styled';
import DOMPurify from 'dompurify';

const styles = (theme, props) => {
  return {
    textField: {
      backgroundColor: theme.colors.eyGrey5,
      height: '48px',
      maxWidth: '300px',
      border: '1px solid transparent',
    },
    ttsOnFocus: {
      top: '-20px',
    },
    textFieldFocus: {
      border: `1px solid ${theme.colors.eyGrey6}`,
    },
    inputRoot: {
      paddingLeft: '7px',
      color: theme.colors.eyConfidentGrey,
      '&:focus': {
        marginTop: '16px',
      },
      '&:focus-within': {
        marginTop: '16px',
      },
      '&:active': {
        marginTop: '14px',
      },
      '&:hover': {
        marginTop: '14px',
      },
    },
    inputDom: {
      width: '95%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'block',
      '&::-ms-clear': {
        display: 'none',
      },
    },
    formLabel: {
      color: theme.colors.eyConfidentGrey,
      paddingLeft: '8px',
      paddingRight: '8px',
      width: '100%',
      height: '100%',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    asterisk: {
      color: theme.colors.red,
      // float: 'right',
      fontSize: '1.2rem',
      paddingRight: '15%',
    },
    formControl: {
      transform: 'translate(0, 16px) scale(1)',
    },
    shrink: {
      transform: 'translate(0, 8px) scale(0.75)',
      marginBottom: '8px',
      color: theme.colors.eyConfidentGrey,
    },
    showPassword: {
      fontFamily: 'caption',
    },
    visibilityIcon: {
      position: 'relative',
      top: '-8px',
    },
    visibilityIconRoot: {
      color: theme.colors.eyGrey2,
    },
    focused: {
      '&$focused': {
        color: theme.colors.eyConfidentGrey,
        transform: 'translate(0, 8px) scale(0.75)',
      },
    },
    hasError: {
      border: `solid 1px ${theme.colors.red}`,
    },
    dateErrorText: {
      color: theme.colors.red,
      margin: '5px 0 5px 5px',
      fontSize: '12px',
    },
    toolTip: {
      display: 'inline-flex',
      width: '100%',
      maxWidth: '300px',
      height: '48px',
    },
    wrapAsterisk: {
      color: theme.colors.red,
      fontSize: '1.2rem',
      paddingRight: '15%',
      display: 'block',
    },
  };
};

class TextInputBase extends React.Component {
  static propTypes = {
    /** Classname to be applied to <TextField> */
    className: PropTypes.string,
    /** If `true`, the input will not have an underline. */
    disableUnderline: PropTypes.bool,
    /** If `true`, password will be visible */
    showPassword: PropTypes.bool,
    /** If `true`, textfield will be in error state */
    hasError: PropTypes.bool,
    /** Override or extend default styles */
    styles: PropTypes.object,
    /** Name attribute of the `input` element. */
    name: PropTypes.string,
    /** Value of `input` element */
    value: PropTypes.string,
    /** Type of the input element. It should be a valid HTML5 input type. */
    type: PropTypes.string,
    /** Text used for input label */
    label: PropTypes.string,
    /** If `true`, the label is displayed as required. */
    required: PropTypes.any,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,
  };

  static defaultProps = {
    styles: {},
    hasError: false,
    disableUnderline: true,
    showPassword: false,
  };

  state = {
    name: this.props.name,
    value: this.props.value || '',
    focused: this.props.focused || false,
    autofillFocus: null,
    passwordVisible: this.props.showPassword || false,
  };

  componentDidUpdate() {
    if (this.state.value !== this.props.value) {
      this.setState({
        value: this.props.value,
      });
    }
  }

  componentDidMount() {
    if (this.inputRef) {
      this.inputRef.addEventListener('animationstart', this.onAnimationStart, false);
    }
  }

  componentWillUnmount() {
    if (this.inputRef) {
      this.inputRef.removeEventListener('animationstart', this.onAnimationStart, false);
    }
  }

  // Chrome when using autofill looks really nasty. The only way around this is to
  // bind an animation event and listen for it. Once it triggers, auto focus the TextField
  onAnimationStart = ({ target, animationName }) => {
    if (animationName === 'onAutoFillStart') {
      this.setState({ autofillFocus: true });
    }
  };

  handleOnChange = evt => {
    this.setState({ value: evt.target.value });
    return (this.props.onChange && this.props.onChange(evt)) || true;
  };

  handleOnFocus = status => {
    this.setState({ focused: status });
    return (this.props.onFocus && this.props.onFocus(status)) || true;
  };

  handleMouseDownPassword = event => {
    event.preventDefault();
  };

  handleClickShowPassword = () => {
    this.setState({ passwordVisible: !this.state.passwordVisible });
  };

  setInputRef = el => {
    if (this.props.inputRef) this.props.inputRef(el);

    this.inputRef = findDOMNode(el);
  };

  render() {
    const {
      classes,
      helperText,
      styles,
      type,
      label,
      hasError,
      errorText,
      className,
      showPassword,
      onChange,
      onFocus,
      disableUnderline,
      inputRef,
      InputLabelProps,
      InputProps,
      maxLength,
      ...otherProps
    } = this.props;

    const textClassName = classNames(
      {
        [classes.textField]: true,
        [classes.textFieldFocus]: this.state.focused,
        [classes.hasError]: this.props.hasError,
        'textinput-root': true,
      },
      className,
    );

    let inputType = type;

    if (inputType === 'password' && this.state.passwordVisible) inputType = 'text';
    return (
      <Fragment>
        <ToolTip
          title={label}
          hideButton={true}
          placement="bottom-start"
          className={classes.toolTip}
          popperClassName={hasError ? 'ttsOnFocusRemove' : 'ttsOnFocus'}>
          <TextField
            helperText={maxLength && this.props.value && `${this.props.value.length}/${maxLength}`}
            type={inputType}
            label={label}
            title={this.state.value}
            className={textClassName}
            onChange={this.handleOnChange}
            onFocus={this.handleOnFocus.bind(null, true)}
            onBlur={this.handleOnFocus.bind(null, false)}
            fullWidth={true}
            inputRef={el => this.setInputRef(el)}
            inputProps={{
              maxLength: maxLength,
            }}
            InputProps={{
              disableUnderline,
              className: classes.inputRoot,
              classes: {
                input: classes.inputDom,
              },
              endAdornment:
                this.props.type === 'password' ? (
                  <InputAdornment position="end" className={classes.visibilityIcon}>
                    <IconButton
                      aria-label="Toggle password visibility"
                      aria-pressed={this.state.passwordVisible}
                      onClick={this.handleClickShowPassword}
                      onMouseDown={this.handleMouseDownPassword}>
                      {this.state.passwordVisible ? (
                        <VisibilityOff className={classNames(classes.visibilityIconRoot, classes.showPassword)} />
                      ) : (
                        <Visibility className={classes.visibilityIconRoot} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ) : null,
              ...InputProps,
            }}
            InputLabelProps={{
              ...(this.state.autofillFocus ? { focused: true } : {}),
              ...(this.state.autofillFocus || this.state.value || (this.props.InputProps && this.props.InputProps.value)
                ? { shrink: true }
                : { shrink: false }),
              classes: {
                formControl: classes.formControl,
                shrink: classes.shrink,
                focused: classes.focused,
                root: classes.formLabel,
                asterisk: this.props.label && this.props.label.length > 30 ? classes.wrapAsterisk : classes.asterisk,
              },
              ...InputLabelProps,
            }}
            FormHelperTextProps={{
              classes: {
                root: classes.formHelperText,
              },
              error: hasError,
            }}
            value={this.state.value}
            {...otherProps}
          />
          {hasError && errorText && (
            <p className={classes.dateErrorText} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(errorText) }} />
          )}
        </ToolTip>
      </Fragment>
    );
  }
}

export { TextInputBase };
export default styled(TextInputBase)(styles);
